import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, formatMoney, resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Globe, Mail, Phone } from "lucide-react";
import { InstagramIcon, FacebookIcon, XIcon, TikTokIcon, WhatsAppIcon, TelegramIcon } from "@/components/BrandIcons";

const SOCIAL_ICONS = [
  { key: "instagram_url", Icon: InstagramIcon, label: "Instagram" },
  { key: "facebook_url", Icon: FacebookIcon, label: "Facebook" },
  { key: "x_url", Icon: XIcon, label: "X" },
  { key: "tiktok_url", Icon: TikTokIcon, label: "TikTok" },
  { key: "whatsapp_url", Icon: WhatsAppIcon, label: "WhatsApp" },
  { key: "telegram_url", Icon: TelegramIcon, label: "Telegram" },
];

export default function PublicVenue() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/venues/${slug}`);
        setData(data);
      } catch (e) {
        setErr("Venue not found");
      }
      try {
        const { data: mem } = await axios.get(`${API}/public/venues/${slug}/memberships`);
        setTiers(mem.tiers || []);
      } catch (e) {
        setTiers([]);
      }
    })();
  }, [slug]);

  if (err) return <div className="min-h-screen grid place-items-center text-zinc-500">{err}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-zinc-500 text-sm">Loading…</div>;

  const { venue, events } = data;
  const brand = venue.brand_color || "#4338CA";

  return (
    <div className="min-h-screen bg-white text-zinc-900" style={{ "--brand": brand }}>
      {/* Header */}
      <header className="border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 py-8 flex items-center gap-4">
          {venue.logo_url ? (
            <img src={resolveImageUrl(venue.logo_url)} alt={venue.name} className="h-12 w-12 object-contain rounded-md" />
          ) : (
            <div className="h-12 w-12 grid place-items-center rounded-md text-white font-display font-black text-lg" style={{ background: brand }}>
              {venue.name?.[0] || "V"}
            </div>
          )}
          <div>
            <h1 className="font-display font-black text-2xl tracking-tight">{venue.name}</h1>
            {venue.description && <p className="text-sm text-zinc-500 mt-0.5">{venue.description}</p>}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {tiers.length > 0 && (
          <section className="mb-14" data-testid="public-membership-section">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Support this venue</div>
            <h2 className="mt-2 font-display font-black text-4xl tracking-tighter">Become a member</h2>
            <p className="mt-2 text-sm text-zinc-600 max-w-lg">
              Recurring supporters keep the lights on. Pick a tier and join in under a minute — you can cancel any time.
            </p>
            <div className="mt-6">
              <Link
                to={`/v/${venue.slug}/membership`}
                data-testid="membership-cta-link"
                className="inline-flex items-center gap-2 text-sm font-semibold px-5 h-11 rounded-full text-white hover:opacity-90 transition"
                style={{ background: brand }}
              >
                Browse membership tiers →
              </Link>
              <div className="mt-3 text-xs text-zinc-500">
                {tiers.length} tier{tiers.length === 1 ? "" : "s"} · from £{Math.min(...tiers.map((t) => t.price_pence / 100)).toFixed(2)}/{tiers[0]?.interval === "annual" ? "year" : tiers[0]?.interval === "six_monthly" ? "6mo" : "month"}
              </div>
            </div>
          </section>
        )}

        <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Upcoming events</div>
        <h2 className="mt-2 font-display font-black text-4xl tracking-tighter">What&apos;s on</h2>

        {events.length === 0 && (
          <div className="mt-10 rounded-xl border border-zinc-200 p-10 text-center text-zinc-500">
            No events on sale right now. Check back soon.

          </div>
        )}

        <div className="mt-8 grid sm:grid-cols-2 gap-6">
          {events.map((e) => {
            const priceFrom = Math.min(...(e.tiers.length ? e.tiers.map((t) => t.price_pence) : [0]));
            return (
              <Link key={e.id} to={`/v/${venue.slug}/e/${e.id}`} className="group rounded-xl overflow-hidden border border-zinc-200 bg-white hover:border-zinc-900 transition-colors duration-200">
                <div className="aspect-[16/9] bg-zinc-100 overflow-hidden">
                  {e.image_url ? (
                    <img src={resolveImageUrl(e.image_url)} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-zinc-400">
                      <Calendar className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="font-display font-bold text-lg tracking-tight">{e.title}</div>
                  <div className="mt-2 text-sm text-zinc-500 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(e.start_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                  {e.venue_location && (
                    <div className="mt-1 text-sm text-zinc-500 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" />
                      {e.venue_location}
                    </div>
                  )}
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest text-zinc-500 font-medium">
                      From {formatMoney(priceFrom)}
                    </span>
                    <Button size="sm" style={{ background: brand }} className="rounded-full text-white hover:opacity-90">
                      Get tickets
                    </Button>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </main>

      <footer className="border-t border-zinc-200 py-6 mt-10">
        <div className="max-w-5xl mx-auto px-6 flex flex-col gap-4">
          {/* Social/messaging icon row */}
          {SOCIAL_ICONS.some((s) => venue[s.key]) && (
            <div className="flex items-center gap-3">
              {SOCIAL_ICONS.filter((s) => venue[s.key]).map(({ key, Icon, label }) => (
                <a
                  key={key}
                  href={venue[key]}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="w-9 h-9 grid place-items-center rounded-full border border-zinc-200 text-zinc-600 hover:text-white hover:border-transparent transition-colors"
                  style={{ transition: "background-color 0.2s, color 0.2s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = brand)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between text-xs text-zinc-500 flex-wrap gap-2">
            <div className="flex items-center gap-4 flex-wrap">
              {venue.website && (
                <a href={venue.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-zinc-900">
                  <Globe className="w-3.5 h-3.5" /> Website
                </a>
              )}
              {venue.contact_email && (
                <a href={`mailto:${venue.contact_email}`} className="flex items-center gap-1 hover:text-zinc-900">
                  <Mail className="w-3.5 h-3.5" /> {venue.contact_email}
                </a>
              )}
              {venue.contact_phone && (
                <a href={`tel:${venue.contact_phone}`} className="flex items-center gap-1 hover:text-zinc-900">
                  <Phone className="w-3.5 h-3.5" /> {venue.contact_phone}
                </a>
              )}
              {venue.address && (
                <span className="flex items-center gap-1 whitespace-pre-line">
                  <MapPin className="w-3.5 h-3.5" /> {venue.address.replace(/\n/g, ", ")}
                </span>
              )}
            </div>
            <div>Powered by <a href="/" className="text-zinc-700 hover:text-zinc-900">Affra</a> · <Link to={`/v/${venue.slug}/terms`} className="hover:text-zinc-900">Terms</Link> · <Link to={`/v/${venue.slug}/privacy`} className="hover:text-zinc-900">Privacy</Link></div>
          </div>
        </div>
      </footer>
    </div>
  );
}
